<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :selectedIndex="selectedIndex"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title
        :baseView="baseView"
        :primaryLabel="`Stocktake # ${baseView.id}`"
      />
    </template>

    <dx-data-grid
      :data-source="stocktakeReviewDataSourceConfig"
      :ref="'stocktake-review-grid'"
      :height="gridHeight"
      @cell-prepared="onCellPrepared"
      :columns="stocktakeReviewGridColumns"
      @toolbar-preparing="onToolbarPreparing($event)"
    >
      <template #primaryImageTemplate="{ data }">
        <GcGridProductImage
          :ItemCode="data.data.ProductCode"
          :HasPrimaryImage="data.data.HasPrimaryImage"
          :Description="data.data.ProductDescription"
        />
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: lineitem }">
        <div>
          <GcBatchStocktakeReview
            :template-data="lineitem"
            :columns="childGridColumns"
          />
        </div>
      </template>
    </dx-data-grid>

    <template #footer>
      <div class="gc-grid-footer">
        <div class="gc-grid-footer-left">
          <div class="gc-grid-footer-item"></div>
        </div>
        <div class="gc-grid-footer-right">
          <div class="gc-grid-footer-item">
            <label class="gc-footer-label"
              >Count Value at Std Cost({{ CurrencyCode }})</label
            >
            <GcFooterValue
              :value="CountValue"
              :format="'Decimal-4-2C'"
              :length="4"
            />
          </div>
          <div class="gc-grid-footer-item">
            <label class="gc-footer-label"
              >Variance Value at Std Cost({{ CurrencyCode }})</label
            >
            <GcFooterValue
              :value="VarianceValue"
              :format="'Decimal-4-2C'"
              :length="4"
            />
          </div>
        </div>
      </div>
    </template>
  </gc-main-layout>
</template>

<script>
import BaseViewMixin from "../../v1/_mixins/base.view";
import { StocktakeReviewService } from "../../_services/api/inventory/stocktake-review.service";
import { DxMasterDetail } from "devextreme-vue/data-grid";
import { CompanyService } from "../../_services/api/accounts/company.service";

var stocktakeReviewDataSource = StocktakeReviewService.dataSourceConfig(
  "api/StockCommitted",
  {
    postProcess: (data) => {
      data.forEach((element) => {
        if (element["IsBatchProduct"] != "No") {
          StocktakeReviewService.getStocktakeReviewBatches(
            element["StocktakeCode"],
            element["ProductCode"]
          ).then((response) => {
            element["StocktakeReviewBatches"] = response.data.value;
          });
        }
        element["CountInCompCurr"] = element["Count"] * element["StandardCost"];
        element["VarianceInCompCurr"] =
          element["Variance"] * element["StandardCost"];
      });
      return data;
    },
    preventPagination: true,
  }
);

export default {
  props: {},
  mixins: [BaseViewMixin],
  components: {
    DxMasterDetail,
  },

  async created() {
    stocktakeReviewDataSource.filter([
      ["StocktakeCode", "=", parseInt(this.baseView.id)],
    ]);
    await stocktakeReviewDataSource.load();
    this.stocktakeReviewDataSourceConfig = stocktakeReviewDataSource.items();

    let res = await CompanyService.getByCompanyCode("01");

    this.CurrencyCode = res.data.value[0].CurrencyCode;
  },
  data() {
    return {
      CurrencyCode: "",
      stocktakeReviewDataSourceConfig: [],
    };
  },
  computed: {
    CountValue() {
      return this.stocktakeReviewDataSourceConfig.reduce((acc, obj) => {
        return acc + obj.CountInCompCurr;
      }, 0);
    },
    VarianceValue() {
      return this.stocktakeReviewDataSourceConfig.reduce((acc, obj) => {
        return acc + obj.VarianceInCompCurr;
      }, 0);
    },
    childGridColumns() {
      return [
        {
          caption: "Batch",
          dataField: "BatchNo",
          editorType: "dxTextBox",
          width: 190,
        },
        {
          caption: "Supplier",
          dataField: "SupplierName",
          editorType: "dxTextBox",
          width: 190,
        },
        {
          caption: "Use By Date",
          editorType: "dxDateBox",
          cellTemplate: "use-by-cell-template",
          editorOptions: {
            displayFormat: "dd MMM yyyy",
          },
          dataField: "UseByDate",
          width: 150,
        },
        {
          caption: "Count",
          editorType: "dxNumberBox",
          dataField: "Count",
          alignment: "right",
          format: this.getDecimalFormatByCode(2).Format,
          width: 100,
        },
      ];
    },
    stocktakeReviewGridColumns() {
      return this.parseGridColumns("transaction-editable", [
        {
          gcLabel: "Item",
          gcType: "String",
          gcSubType: "PrimaryRecord",
          dataField: "ProductDescription",
          cellTemplate: "primaryImageTemplate",
        },
        {
          gcLabel: "Item",
          dataField: "ProductCode",
          width: 120,
          gcType: "String",
          gcSubType: "HiddenCode",
        },
        {
          gcLabel: "Description",
          dataField: "ProductDescription",
          minWidth: this.gridColumnMinWidth(220),
          gcType: "String",
          gcSubType: "HiddenDescription",
        },

        {
          gcLabel: "Branch",
          dataField: "BranchDescription",
          gcType: "String",
          width: 150,
        },
        {
          gcLabel: "UM",
          dataField: "UnitOfMeasureCode",
          gcType: "String",
          width: 100,
        },

        {
          gcLabel: "Count",
          dataField: "Count",
          gcType: "Decimal-6-2",
          allowFiltering: false,
        },

        {
          gcLabel: "Variance",
          dataField: "Variance",
          gcType: "Decimal-6-2",
          allowFiltering: false,
        },

        {
          gcDynamicLabel: `Std Cost(${this.CurrencyCode})`,
          dataField: "StandardCost",
          gcType: "Decimal-6-4",
          allowFiltering: false,
        },

        {
          gcDynamicLabel: `Count(${this.CurrencyCode})`,
          dataField: "CountInCompCurr",
          gcType: "Decimal-6-2",
          allowFiltering: false,
        },

        {
          gcDynamicLabel: `Variance(${this.CurrencyCode})`,
          dataField: "VarianceInCompCurr",
          gcType: "Decimal-6-2",
          allowFiltering: false,
        },
      ]);
    },
  },
  methods: {
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      var columnChooserButton = toolbarItems.find((item) => {
        return item.name === "columnChooserButton";
      });

      e.toolbarOptions.items = toolbarItems.filter((item) => {
        return (
          item.name !== "searchPanel" && item.name !== "columnChooserButton"
        );
      });

      searchPanel.location = "after";

      e.toolbarOptions.items.unshift(searchPanel, columnChooserButton);
    },
    onCellPrepared(e) {
      if (e.rowType === "data" && e.column.command === "expand") {
        if (e.data.IsBatchProduct == "No") {
          e.cellElement.classList.remove("dx-datagrid-expand");
          e.cellElement.childNodes[0].classList.remove(
            "dx-datagrid-group-closed"
          );
        }
      }
    },
    backButtonFunction() {
      this.$router.push({
        path: "/stocktake",
      });
    },
  },
};
</script>
